/**
 * useNotifications Hook
 * CCH Care — Phase 2
 *
 * Manages notification list, unread count, toast queue, and polling.
 *
 * Changes (2026-05-13):
 * - addToast now exposed in the return object so external components
 *   (AccessDeniedHandler, SessionTimeout warnings, etc.) can push
 *   ad-hoc toasts through the same UI surface used for server-driven
 *   notification toasts. Previously addToast was internal-only, used
 *   solely by fetchNotifications to fire toasts on brand-new items.
 *
 * ESLint fix:
 * - addToast added to fetchNotifications useCallback dependency array
 *   (addToast is stable — empty [] deps — so this is safe and correct)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getNotifications, markRead, markAllRead } from '../api/notifications';

export default function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [toasts, setToasts]               = useState([]);
  const [panelOpen, setPanelOpen]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const lastFetchRef = useRef(null);
  const toastIdRef   = useRef(0);

  // Toast management — stable, no deps.
  // Accepts any notification-shaped object: { priority, title, message, ... }.
  // The `priority` field drives duration via durationMap; other fields are
  // spread into the toast for ToastContainer to render however it likes.
  const addToast = useCallback((notification) => {
    const id = ++toastIdRef.current;
    const durationMap = { urgent: 5000, important: 4000, info: 3000 };
    const duration = durationMap[notification.priority] || 3000;

    setToasts(prev => {
      // Max 3 visible toasts
      const next = [...prev, { ...notification, toastId: id }];
      return next.slice(-3);
    });

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.toastId !== id));
    }, duration);
  }, []);

  const dismissToast = useCallback((toastId) => {
    setToasts(prev => prev.filter(t => t.toastId !== toastId));
  }, []);

  // Fetch notifications — addToast is a stable dep (empty [] above)
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNotifications({ limit: 30 });
      if (data.success) {
        const newNotifs = data.notifications || [];

        // Detect NEW notifications since last fetch — fire toasts
        if (lastFetchRef.current !== null) {
          const prevIds  = new Set(lastFetchRef.current.map(n => n.notification_id));
          const brandNew = newNotifs.filter(n => !prevIds.has(n.notification_id) && !n.is_read);
          brandNew.forEach(n => addToast(n));
        }

        lastFetchRef.current = newNotifs;
        setNotifications(newNotifs);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Notifications fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // Mark single notification read
  const handleMarkRead = useCallback(async (notificationId) => {
    try {
      await markRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark read error:', err);
    }
  }, []);

  // Mark all read
  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all read error:', err);
    }
  }, []);

  // Poll every 60 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    toasts,
    panelOpen,
    setPanelOpen,
    loading,
    addToast,                       // ← exposed for ad-hoc toasts
    dismissToast,
    markRead:    handleMarkRead,
    markAllRead: handleMarkAllRead,
    refresh:     fetchNotifications,
  };
}